import React from "react";
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { formatClientPrefix } from "../../../../../utils/clientPrefix";
import { useTreeViewContext } from "../TreeViewContext";
import {
  deletePlantWbsCountSuffix,
  plantNameParts,
} from "../utils/plantNameHelpers";

// Right-click menu on a project row - the WBS one below, a level up.
const PlantContextMenu = () => {
  const { plantOperations } = useTreeViewContext();
  const { plantMenu, closePlantMenu, openEditPlant, openDeletePlant } =
    plantOperations;

  return (
    <Menu
      open={Boolean(plantMenu)}
      onClose={closePlantMenu}
      anchorReference="anchorPosition"
      anchorPosition={
        plantMenu
          ? { top: plantMenu.mouseY, left: plantMenu.mouseX }
          : undefined
      }
    >
      <MenuItem onClick={() => openEditPlant(plantMenu.plant)}>
        <ListItemIcon>
          <EditIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>Modify</ListItemText>
      </MenuItem>
      <MenuItem
        onClick={() => openDeletePlant(plantMenu.plant)}
        sx={{ color: "error.main" }}
      >
        <ListItemIcon>
          <DeleteOutlineIcon fontSize="small" color="error" />
        </ListItemIcon>
        <ListItemText>Delete</ListItemText>
      </MenuItem>
    </Menu>
  );
};

// Modify a project: the number half of its name and its description. The
// client is not offered - it sets the name's prefix, and every WBS under
// here was created under it.
const EditPlantDialog = () => {
  const { plantOperations } = useTreeViewContext();
  const {
    editPlantTarget,
    setEditPlantTarget,
    editPlantForm,
    setEditPlantForm,
    savingPlantEdit,
    editPlantError,
    handleUpdatePlant,
  } = plantOperations;

  return (
    <Dialog
      open={Boolean(editPlantTarget)}
      onClose={() => !savingPlantEdit && setEditPlantTarget(null)}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>Modify Project — {editPlantTarget?.name}</DialogTitle>
      <DialogContent>
        <Box
          sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 1,
            }}
          >
            <TextField
              label="Prefix"
              value={plantNameParts(editPlantTarget).prefix}
              disabled
              placeholder="—"
              sx={{ flex: "1 1 100px", minWidth: 90 }}
            />
            <Box component="span" sx={{ fontWeight: 600, pt: 2 }}>
              -
            </Box>
            <TextField
              label="Number"
              value={editPlantForm.number}
              onChange={(event) =>
                setEditPlantForm((prev) => ({
                  ...prev,
                  number: event.target.value,
                }))
              }
              required
              autoFocus
              sx={{ flex: "1 1 140px", minWidth: 120 }}
              inputProps={{ maxLength: 20 }}
            />
          </Box>
          <Typography variant="caption" color="text.secondary">
            Name preview:{" "}
            {[plantNameParts(editPlantTarget).prefix, editPlantForm.number]
              .filter(Boolean)
              .join("-") || "—"}
          </Typography>
          <TextField
            label="Description (optional)"
            value={editPlantForm.description}
            onChange={(event) =>
              setEditPlantForm((prev) => ({
                ...prev,
                description: event.target.value,
              }))
            }
            helperText="Shown under Project# UID in the module headers"
            fullWidth
            multiline
            minRows={2}
          />
          {editPlantError && (
            <Typography variant="caption" color="error">
              {editPlantError}
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => setEditPlantTarget(null)}
          disabled={savingPlantEdit}
        >
          Cancel
        </Button>
        <Button
          onClick={handleUpdatePlant}
          variant="contained"
          disabled={savingPlantEdit}
        >
          {savingPlantEdit ? "Saving…" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Delete a project. Spells out the WBS count when this plant's list has
// been loaded - deleting takes all of them with it.
const DeletePlantDialog = () => {
  const { plantOperations } = useTreeViewContext();
  const {
    wbsByPlant,
    deletePlantTarget,
    setDeletePlantTarget,
    deletingPlant,
    deletePlantError,
    handleDeletePlant,
  } = plantOperations;

  return (
    <Dialog
      open={Boolean(deletePlantTarget)}
      onClose={() => !deletingPlant && setDeletePlantTarget(null)}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>Delete Project?</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ wordBreak: "break-word" }}>
          {`Delete ${deletePlantTarget?.name || "this project"}${deletePlantWbsCountSuffix(deletePlantTarget, wbsByPlant)}? This cannot be undone.`}
        </DialogContentText>
        {deletePlantError && (
          <Typography
            variant="caption"
            color="error"
            sx={{ display: "block", mt: 2 }}
          >
            {deletePlantError}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => setDeletePlantTarget(null)}
          disabled={deletingPlant}
        >
          Cancel
        </Button>
        <Button
          onClick={handleDeletePlant}
          variant="contained"
          color="error"
          disabled={deletingPlant}
        >
          {deletingPlant ? "Deleting…" : "Delete"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Same fields as the Projects page's "Create New Project" dialog
// (Project/plant/index.jsx): the name is the client's prefix and a number,
// so a project can't be named outside its client's scheme.
const AddPlantDialog = () => {
  const { plantOperations } = useTreeViewContext();
  const {
    addPlantOpen,
    setAddPlantOpen,
    plantForm,
    setPlantForm,
    savingPlant,
    plantError,
    clients,
    clientsLoading,
    handleCreatePlant,
  } = plantOperations;

  return (
    <Dialog
      open={addPlantOpen}
      onClose={() => !savingPlant && setAddPlantOpen(false)}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>Add Project</DialogTitle>
      <DialogContent>
        <Box
          sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}
        >
          <Autocomplete
            options={clients}
            loading={clientsLoading}
            getOptionLabel={(option) =>
              option?.name
                ? `${option.name}${option.prefix ? ` (${formatClientPrefix(option.prefix)})` : ""}`
                : ""
            }
            isOptionEqualToValue={(option, value) =>
              option?.id === value?.id
            }
            value={
              clients.find((client) => client.id === plantForm.clientId) || null
            }
            onChange={(_event, value) =>
              setPlantForm((prev) => ({
                ...prev,
                clientId: value?.id || "",
                namePrefix: formatClientPrefix(value?.prefix || value?.name),
              }))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Client"
                required
                helperText="The name prefix comes from the client"
              />
            )}
          />
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 1,
            }}
          >
            <TextField
              label="Prefix"
              value={plantForm.namePrefix}
              disabled
              placeholder="Select client"
              sx={{ flex: "1 1 100px", minWidth: 90 }}
            />
            <Box component="span" sx={{ fontWeight: 600, pt: 2 }}>
              -
            </Box>
            <TextField
              label="Number"
              value={plantForm.nameNumber}
              onChange={(event) =>
                setPlantForm((prev) => ({
                  ...prev,
                  nameNumber: event.target.value,
                }))
              }
              required
              placeholder="p001"
              helperText="e.g. p001"
              sx={{ flex: "1 1 140px", minWidth: 120 }}
              inputProps={{ maxLength: 20 }}
            />
          </Box>
          <Typography variant="caption" color="text.secondary">
            Name preview:{" "}
            {[plantForm.namePrefix, plantForm.nameNumber]
              .filter(Boolean)
              .join("-") || "—"}
          </Typography>
          <TextField
            label="Description (optional)"
            value={plantForm.description}
            onChange={(event) =>
              setPlantForm((prev) => ({
                ...prev,
                description: event.target.value,
              }))
            }
            fullWidth
            multiline
            minRows={2}
          />
          <TextField
            label="SharePoint Link (optional)"
            value={plantForm.sharepointLink}
            onChange={(event) =>
              setPlantForm((prev) => ({
                ...prev,
                sharepointLink: event.target.value,
              }))
            }
            fullWidth
            type="url"
          />
          {plantError && (
            <Typography variant="caption" color="error">
              {plantError}
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => setAddPlantOpen(false)}
          disabled={savingPlant}
        >
          Cancel
        </Button>
        <Button
          onClick={handleCreatePlant}
          variant="contained"
          disabled={savingPlant}
        >
          {savingPlant ? "Creating…" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const PlantDialogs = () => (
  <>
    <PlantContextMenu />
    <EditPlantDialog />
    <DeletePlantDialog />
    <AddPlantDialog />
  </>
);
